import React, { useEffect, useRef, useState } from "react";
import io from "socket.io-client";

import { Badge, IconButton, TextField, Button } from "@mui/material";

import VideocamIcon from "@mui/icons-material/Videocam";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";
import CallEndIcon from "@mui/icons-material/CallEnd";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import ScreenShareIcon from "@mui/icons-material/ScreenShare";
import StopScreenShareIcon from "@mui/icons-material/StopScreenShare";
import ChatIcon from "@mui/icons-material/Chat";
import CloseIcon from "@mui/icons-material/Close";

import styles from "../styles/videoComponent.module.css";
import server from "../environment";

const server_url = server;

const peerConfigConnections = {
    iceServers: [
        {
            urls: "stun:stun.l.google.com:19302",
        },
    ],
};

export default function VideoMeetComponent() {
    const socketRef = useRef(null);
    const socketIdRef = useRef(null);

    const localVideoref = useRef(null);

    const connections = useRef({});
    const remoteStreams = useRef({});
    const pendingCandidates = useRef({});

    const [videoAvailable, setVideoAvailable] = useState(true);
    const [audioAvailable, setAudioAvailable] = useState(true);

    const [video, setVideo] = useState(true);
    const [audio, setAudio] = useState(true);

    const [screen, setScreen] = useState(false);
    const [screenAvailable, setScreenAvailable] = useState(false);

    const [videos, setVideos] = useState([]);

    const [askForUsername, setAskForUsername] = useState(true);
    const [username, setUsername] = useState("");

    const [showModal, setModal] = useState(false);

    const [messages, setMessages] = useState([]);
    const [message, setMessage] = useState("");
    const [newMessages, setNewMessages] = useState(0);

    // =========================================================
    // GET PERMISSIONS
    // =========================================================

    useEffect(() => {
        getPermissions();

        return () => {
            cleanupConnections();
        };
    }, []);

    const getPermissions = async () => {
        try {
            const videoPermission =
                await navigator.mediaDevices.getUserMedia({
                    video: true,
                });

            setVideoAvailable(!!videoPermission);

            videoPermission.getTracks().forEach((track) => {
                track.stop();
            });
        } catch (error) {
            console.log("Video permission denied:", error);
            setVideoAvailable(false);
        }

        try {
            const audioPermission =
                await navigator.mediaDevices.getUserMedia({
                    audio: true,
                });

            setAudioAvailable(!!audioPermission);

            audioPermission.getTracks().forEach((track) => {
                track.stop();
            });
        } catch (error) {
            console.log("Audio permission denied:", error);
            setAudioAvailable(false);
        }

        setScreenAvailable(
            !!navigator.mediaDevices.getDisplayMedia
        );
    };

    // =========================================================
    // START CALL
    // =========================================================

    const connect = async () => {
        if (!username.trim()) {
            return;
        }

        setAskForUsername(false);

        try {
            const stream =
                await navigator.mediaDevices.getUserMedia({
                    video: videoAvailable,
                    audio: audioAvailable,
                });

            window.localStream = stream;

            if (localVideoref.current) {
                localVideoref.current.srcObject = stream;
            }

            connectToSocketServer();
        } catch (error) {
            console.error("Unable to start camera/microphone:", error);

            // Still allow call if permissions were denied
            const emptyStream = new MediaStream();

            window.localStream = emptyStream;

            if (localVideoref.current) {
                localVideoref.current.srcObject = emptyStream;
            }

            connectToSocketServer();
        }
    };

    // =========================================================
    // CREATE PEER CONNECTION
    // =========================================================

    const createPeerConnection = (remoteId) => {
        if (connections.current[remoteId]) {
            return connections.current[remoteId];
        }

        console.log("Creating peer connection:", remoteId);

        const peerConnection =
            new RTCPeerConnection(peerConfigConnections);

        connections.current[remoteId] = peerConnection;

        pendingCandidates.current[remoteId] = [];

        // -----------------------------------------------------
        // ICE
        // -----------------------------------------------------

        peerConnection.onicecandidate = (event) => {
            if (event.candidate) {
                socketRef.current?.emit(
                    "signal",
                    remoteId,
                    JSON.stringify({
                        ice: event.candidate,
                    })
                );
            }
        };

        // -----------------------------------------------------
        // IMPORTANT:
        // RECEIVE REMOTE VIDEO
        // -----------------------------------------------------

        peerConnection.ontrack = (event) => {
            console.log(
                "Remote track received from:",
                remoteId,
                event.streams
            );

            let stream;

            if (event.streams && event.streams[0]) {
                stream = event.streams[0];
            } else {
                stream = remoteStreams.current[remoteId];

                if (!stream) {
                    stream = new MediaStream();
                    remoteStreams.current[remoteId] = stream;
                }

                if (
                    !stream
                        .getTracks()
                        .some((track) => track.id === event.track.id)
                ) {
                    stream.addTrack(event.track);
                }
            }

            remoteStreams.current[remoteId] = stream;

            setVideos((prevVideos) => {
                const existing = prevVideos.find(
                    (v) => v.socketId === remoteId
                );

                if (existing) {
                    return prevVideos.map((v) =>
                        v.socketId === remoteId
                            ? {
                                  ...v,
                                  stream,
                              }
                            : v
                    );
                }

                return [
                    ...prevVideos,
                    {
                        socketId: remoteId,
                        stream,
                    },
                ];
            });
        };

        peerConnection.onconnectionstatechange = () => {
            console.log(
                `Connection ${remoteId}:`,
                peerConnection.connectionState
            );

            if (
                peerConnection.connectionState === "failed" ||
                peerConnection.connectionState === "closed"
            ) {
                removePeer(remoteId);
            }
        };

        // -----------------------------------------------------
        // ADD LOCAL TRACKS
        // -----------------------------------------------------

        if (window.localStream) {
            window.localStream.getTracks().forEach((track) => {
                try {
                    peerConnection.addTrack(
                        track,
                        window.localStream
                    );
                } catch (error) {
                    console.log(
                        "Track already added:",
                        error
                    );
                }
            });
        }

        return peerConnection;
    };

    // =========================================================
    // SOCKET CONNECTION
    // =========================================================

    const connectToSocketServer = () => {
        if (socketRef.current) {
            return;
        }

        console.log("Connecting socket:", server_url);

        socketRef.current = io(server_url, {
            transports: ["websocket"],
        });

        socketRef.current.on("connect", () => {
            console.log(
                "Socket connected:",
                socketRef.current.id
            );

            socketIdRef.current =
                socketRef.current.id;

            socketRef.current.emit(
                "join-call",
                window.location.href
            );
        });

        socketRef.current.on(
            "signal",
            gotMessageFromServer
        );

        socketRef.current.on(
            "chat-message",
            addMessage
        );

        // =====================================================
        // USER JOINED
        // =====================================================

        socketRef.current.on(
            "user-joined",
            async (id, clients) => {
                console.log(
                    "User joined:",
                    id,
                    clients
                );

                // Create connections for every other client
                for (const clientId of clients) {
                    if (
                        clientId ===
                        socketIdRef.current
                    ) {
                        continue;
                    }

                    if (
                        !connections.current[
                            clientId
                        ]
                    ) {
                        createPeerConnection(
                            clientId
                        );
                    }
                }

                // Only the newly joined client creates offers.
                if (
                    id === socketIdRef.current
                ) {
                    for (const clientId of clients) {
                        if (
                            clientId ===
                            socketIdRef.current
                        ) {
                            continue;
                        }

                        const pc =
                            connections.current[
                                clientId
                            ];

                        if (!pc) continue;

                        try {
                            const offer =
                                await pc.createOffer();

                            await pc.setLocalDescription(
                                offer
                            );

                            socketRef.current.emit(
                                "signal",
                                clientId,
                                JSON.stringify({
                                    sdp: pc.localDescription,
                                })
                            );
                        } catch (error) {
                            console.error(
                                "Offer error:",
                                error
                            );
                        }
                    }
                }
            }
        );

        // =====================================================
        // USER LEFT
        // =====================================================

        socketRef.current.on(
            "user-left",
            (id) => {
                console.log(
                    "User left:",
                    id
                );

                removePeer(id);
            }
        );
    };

    // =========================================================
    // RECEIVE SIGNAL
    // =========================================================

    const gotMessageFromServer = async (
        fromId,
        message
    ) => {
        if (
            fromId === socketIdRef.current
        ) {
            return;
        }

        try {
            const signal =
                JSON.parse(message);

            // Create connection if it doesn't exist
            const pc =
                connections.current[fromId] ||
                createPeerConnection(fromId);

            // -------------------------------------------------
            // SDP
            // -------------------------------------------------

            if (signal.sdp) {
                await pc.setRemoteDescription(
                    new RTCSessionDescription(
                        signal.sdp
                    )
                );

                // Add queued ICE candidates
                const queuedCandidates =
                    pendingCandidates.current[
                        fromId
                    ] || [];

                for (const candidate of queuedCandidates) {
                    try {
                        await pc.addIceCandidate(
                            candidate
                        );
                    } catch (error) {
                        console.log(
                            "Queued ICE error:",
                            error
                        );
                    }
                }

                pendingCandidates.current[
                    fromId
                ] = [];

                // -------------------------------------------------
                // OFFER -> ANSWER
                // -------------------------------------------------

                if (
                    signal.sdp.type ===
                    "offer"
                ) {
                    const answer =
                        await pc.createAnswer();

                    await pc.setLocalDescription(
                        answer
                    );

                    socketRef.current.emit(
                        "signal",
                        fromId,
                        JSON.stringify({
                            sdp: pc.localDescription,
                        })
                    );
                }
            }

            // -------------------------------------------------
            // ICE
            // -------------------------------------------------

            if (signal.ice) {
                const candidate =
                    new RTCIceCandidate(
                        signal.ice
                    );

                if (
                    pc.remoteDescription &&
                    pc.remoteDescription.type
                ) {
                    try {
                        await pc.addIceCandidate(
                            candidate
                        );
                    } catch (error) {
                        console.log(
                            "ICE error:",
                            error
                        );
                    }
                } else {
                    if (
                        !pendingCandidates.current[
                            fromId
                        ]
                    ) {
                        pendingCandidates.current[
                            fromId
                        ] = [];
                    }

                    pendingCandidates.current[
                        fromId
                    ].push(candidate);
                }
            }
        } catch (error) {
            console.error(
                "Signal processing error:",
                error
            );
        }
    };

    // =========================================================
    // REMOVE PEER
    // =========================================================

    const removePeer = (id) => {
        if (connections.current[id]) {
            try {
                connections.current[
                    id
                ].close();
            } catch (error) {
                console.log(error);
            }

            delete connections.current[id];
        }

        delete remoteStreams.current[id];
        delete pendingCandidates.current[id];

        setVideos((prevVideos) =>
            prevVideos.filter(
                (video) =>
                    video.socketId !== id
            )
        );
    };

    // =========================================================
    // CLEANUP
    // =========================================================

    const cleanupConnections = () => {
        Object.values(
            connections.current
        ).forEach((connection) => {
            try {
                connection.close();
            } catch (error) {}
        });

        connections.current = {};
        remoteStreams.current = {};
        pendingCandidates.current = {};

        if (window.localStream) {
            window.localStream
                .getTracks()
                .forEach((track) =>
                    track.stop()
                );
        }

        if (socketRef.current) {
            socketRef.current.disconnect();
            socketRef.current = null;
        }
    };

    // =========================================================
    // REPLACE LOCAL TRACK
    // =========================================================

    const replaceVideoTrack = async (
        newTrack
    ) => {
        const peers = Object.values(
            connections.current
        );

        for (const pc of peers) {
            const sender =
                pc.getSenders().find(
                    (s) =>
                        s.track &&
                        s.track.kind ===
                            "video"
                );

            if (sender) {
                await sender.replaceTrack(
                    newTrack
                );
            } else if (newTrack) {
                pc.addTrack(
                    newTrack,
                    window.localStream
                );
            }
        }
    };

    const replaceAudioTrack = async (
        newTrack
    ) => {
        const peers = Object.values(
            connections.current
        );

        for (const pc of peers) {
            const sender =
                pc.getSenders().find(
                    (s) =>
                        s.track &&
                        s.track.kind ===
                            "audio"
                );

            if (sender) {
                await sender.replaceTrack(
                    newTrack
                );
            } else if (newTrack) {
                pc.addTrack(
                    newTrack,
                    window.localStream
                );
            }
        }
    };

    // =========================================================
    // CAMERA
    // =========================================================

    const handleVideo = async () => {
        if (!window.localStream) return;

        const currentTrack =
            window.localStream.getVideoTracks()[0];

        if (currentTrack) {
            currentTrack.enabled =
                !currentTrack.enabled;

            setVideo(
                currentTrack.enabled
            );
        } else {
            try {
                const stream =
                    await navigator.mediaDevices.getUserMedia(
                        {
                            video: true,
                            audio: false,
                        }
                    );

                const newTrack =
                    stream.getVideoTracks()[0];

                window.localStream.addTrack(
                    newTrack
                );

                if (
                    localVideoref.current
                ) {
                    localVideoref.current.srcObject =
                        window.localStream;
                }

                await replaceVideoTrack(
                    newTrack
                );

                setVideo(true);
            } catch (error) {
                console.error(
                    "Camera error:",
                    error
                );
            }
        }
    };

    // =========================================================
    // MICROPHONE
    // =========================================================

    const handleAudio = () => {
        if (!window.localStream) return;

        const audioTrack =
            window.localStream.getAudioTracks()[0];

        if (audioTrack) {
            audioTrack.enabled =
                !audioTrack.enabled;

            setAudio(
                audioTrack.enabled
            );
        }
    };

    // =========================================================
    // SCREEN SHARE
    // =========================================================

    const handleScreen = async () => {
        if (screen) {
            stopScreenShare();
            return;
        }

        try {
            const displayStream =
                await navigator.mediaDevices.getDisplayMedia(
                    {
                        video: true,
                        audio: true,
                    }
                );

            const screenTrack =
                displayStream.getVideoTracks()[0];

            const cameraTrack =
                window.localStream?.getVideoTracks()[0];

            await replaceVideoTrack(
                screenTrack
            );

            if (localVideoref.current) {
                localVideoref.current.srcObject =
                    displayStream;
            }

            setScreen(true);

            screenTrack.onended = () => {
                restoreCamera(
                    cameraTrack
                );
            };
        } catch (error) {
            console.log(
                "Screen share cancelled:",
                error
            );

            setScreen(false);
        }
    };

    const restoreCamera = async (
        cameraTrack
    ) => {
        if (!cameraTrack) {
            setScreen(false);
            return;
        }

        try {
            await replaceVideoTrack(
                cameraTrack
            );

            if (localVideoref.current) {
                localVideoref.current.srcObject =
                    window.localStream;
            }

            setScreen(false);
        } catch (error) {
            console.error(
                "Restore camera error:",
                error
            );
        }
    };

    const stopScreenShare = async () => {
        const cameraTrack =
            window.localStream?.getVideoTracks()[0];

        await restoreCamera(cameraTrack);
    };

    // =========================================================
    // END CALL
    // =========================================================

    const handleEndCall = () => {
        cleanupConnections();

        window.location.href = "/";
    };

    // =========================================================
    // CHAT
    // =========================================================

    const openChat = () => {
        setModal(true);
        setNewMessages(0);
    };

    const closeChat = () => {
        setModal(false);
    };

    const handleMessage = (e) => {
        setMessage(e.target.value);
    };

    const addMessage = (
        data,
        sender,
        socketIdSender
    ) => {
        setMessages((prevMessages) => [
            ...prevMessages,
            {
                sender,
                data,
            },
        ]);

        if (
            socketIdSender !==
            socketIdRef.current
        ) {
            setNewMessages(
                (prev) => prev + 1
            );
        }
    };

    const sendMessage = () => {
        if (!message.trim()) {
            return;
        }

        if (socketRef.current) {
            socketRef.current.emit(
                "chat-message",
                message,
                username
            );
        }

        setMessage("");
    };

    const handleChatKeyDown = (e) => {
        if (
            e.key === "Enter" &&
            !e.shiftKey
        ) {
            e.preventDefault();
            sendMessage();
        }
    };

    // =========================================================
    // UI
    // =========================================================

    return (
        <div className={styles.page}>
            {askForUsername ? (
                // =================================================
                // LOBBY
                // =================================================
                <div className={styles.lobby}>
                    <div
                        className={
                            styles.lobbyCard
                        }
                    >
                        <h1>
                            Ready to join?
                        </h1>

                        <p>
                            Enter your name
                            to join the meeting.
                        </p>

                        <TextField
                            fullWidth
                            label="Your name"
                            value={username}
                            onChange={(e) =>
                                setUsername(
                                    e.target.value
                                )
                            }
                            onKeyDown={(e) => {
                                if (
                                    e.key ===
                                    "Enter"
                                ) {
                                    connect();
                                }
                            }}
                        />

                        <Button
                            fullWidth
                            variant="contained"
                            onClick={connect}
                            className={
                                styles.connectButton
                            }
                        >
                            Join Meeting
                        </Button>

                        <div
                            className={
                                styles.lobbyPreview
                            }
                        >
                            <video
                                ref={
                                    localVideoref
                                }
                                autoPlay
                                muted
                                playsInline
                            />
                        </div>
                    </div>
                </div>
            ) : (
                // =================================================
                // MEETING
                // =================================================
                <div
                    className={
                        styles.meetVideoContainer
                    }
                >
                    {/* REMOTE VIDEOS */}

                    <div
                        className={
                            videos.length === 0
                                ? styles.conferenceViewEmpty
                                : styles.conferenceView
                        }
                    >
                        {videos.map(
                            (remoteVideo) => (
                                <div
                                    className={
                                        styles.remoteVideoWrapper
                                    }
                                    key={
                                        remoteVideo.socketId
                                    }
                                >
                                    <video
                                        className={
                                            styles.remoteVideo
                                        }
                                        ref={(ref) => {
                                            if (
                                                ref &&
                                                remoteVideo.stream &&
                                                ref.srcObject !==
                                                    remoteVideo.stream
                                            ) {
                                                ref.srcObject =
                                                    remoteVideo.stream;
                                            }
                                        }}
                                        autoPlay
                                        playsInline
                                    />

                                    <span
                                        className={
                                            styles.remoteLabel
                                        }
                                    >
                                        Participant
                                    </span>
                                </div>
                            )
                        )}
                    </div>

                    {/* NO REMOTE USER */}

                    {videos.length === 0 && (
                        <div
                            className={
                                styles.waitingMessage
                            }
                        >
                            <div>
                                <h2>
                                    Waiting for
                                    someone to join...
                                </h2>

                                <p>
                                    Share your meeting
                                    link with another
                                    participant.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* LOCAL VIDEO */}

                    <div
                        className={
                            styles.localVideoWrapper
                        }
                    >
                        <video
                            className={
                                styles.meetUserVideo
                            }
                            ref={
                                localVideoref
                            }
                            autoPlay
                            muted
                            playsInline
                        />

                        <span
                            className={
                                styles.youLabel
                            }
                        >
                            You
                        </span>
                    </div>

                    {/* CHAT */}

                    {showModal && (
                        <div
                            className={
                                styles.chatRoom
                            }
                        >
                            <div
                                className={
                                    styles.chatContainer
                                }
                            >
                                {/* HEADER */}

                                <div
                                    className={
                                        styles.chatHeader
                                    }
                                >
                                    <div>
                                        <h2>
                                            Chat
                                        </h2>

                                        <span>
                                            {
                                                messages.length
                                            }{" "}
                                            messages
                                        </span>
                                    </div>

                                    {/* CLOSE BUTTON */}

                                    <IconButton
                                        onClick={
                                            closeChat
                                        }
                                        className={
                                            styles.closeChatButton
                                        }
                                        aria-label="Close chat"
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                </div>

                                {/* MESSAGES */}

                                <div
                                    className={
                                        styles.chattingDisplay
                                    }
                                >
                                    {messages.length >
                                    0 ? (
                                        messages.map(
                                            (
                                                item,
                                                index
                                            ) => (
                                                <div
                                                    className={
                                                        item.sender ===
                                                        username
                                                            ? styles.myMessage
                                                            : styles.otherMessage
                                                    }
                                                    key={
                                                        index
                                                    }
                                                >
                                                    <strong>
                                                        {
                                                            item.sender
                                                        }
                                                    </strong>

                                                    <p>
                                                        {
                                                            item.data
                                                        }
                                                    </p>
                                                </div>
                                            )
                                        )
                                    ) : (
                                        <div
                                            className={
                                                styles.noMessages
                                            }
                                        >
                                            No messages
                                            yet
                                        </div>
                                    )}
                                </div>

                                {/* INPUT */}

                                <div
                                    className={
                                        styles.chattingArea
                                    }
                                >
                                    <TextField
                                        value={
                                            message
                                        }
                                        onChange={
                                            handleMessage
                                        }
                                        onKeyDown={
                                            handleChatKeyDown
                                        }
                                        fullWidth
                                        placeholder="Type a message..."
                                        variant="outlined"
                                        size="small"
                                    />

                                    <Button
                                        variant="contained"
                                        onClick={
                                            sendMessage
                                        }
                                    >
                                        Send
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* CONTROL BAR */}

                    <div
                        className={
                            styles.buttonContainers
                        }
                    >
                        {/* CAMERA */}

                        <IconButton
                            onClick={
                                handleVideo
                            }
                            className={
                                video
                                    ? styles.controlButton
                                    : styles.controlButtonOff
                            }
                            aria-label="Toggle camera"
                        >
                            {video ? (
                                <VideocamIcon />
                            ) : (
                                <VideocamOffIcon />
                            )}
                        </IconButton>

                        {/* END CALL */}

                        <IconButton
                            onClick={
                                handleEndCall
                            }
                            className={
                                styles.endCallButton
                            }
                            aria-label="End call"
                        >
                            <CallEndIcon />
                        </IconButton>

                        {/* MICROPHONE */}

                        <IconButton
                            onClick={
                                handleAudio
                            }
                            className={
                                audio
                                    ? styles.controlButton
                                    : styles.controlButtonOff
                            }
                            aria-label="Toggle microphone"
                        >
                            {audio ? (
                                <MicIcon />
                            ) : (
                                <MicOffIcon />
                            )}
                        </IconButton>

                        {/* SCREEN SHARE */}

                        {screenAvailable && (
                            <IconButton
                                onClick={
                                    handleScreen
                                }
                                className={
                                    screen
                                        ? styles.controlButtonActive
                                        : styles.controlButton
                                }
                                aria-label="Screen share"
                            >
                                {screen ? (
                                    <StopScreenShareIcon />
                                ) : (
                                    <ScreenShareIcon />
                                )}
                            </IconButton>
                        )}

                        {/* CHAT */}

                        <Badge
                            badgeContent={
                                newMessages
                            }
                            max={999}
                            color="error"
                        >
                            <IconButton
                                onClick={
                                    showModal
                                        ? closeChat
                                        : openChat
                                }
                                className={
                                    styles.controlButton
                                }
                                aria-label={
                                    showModal
                                        ? "Close chat"
                                        : "Open chat"
                                }
                            >
                                {showModal ? (
                                    <CloseIcon />
                                ) : (
                                    <ChatIcon />
                                )}
                            </IconButton>
                        </Badge>
                    </div>
                </div>
            )}
        </div>
    );
}